import { FloatingInput } from '@/components/ui/floating-input';
import { cn } from '@/lib/utils';
import {
  BytesValidationState,
  TransactionBuilderParameterInputProps,
  TransactionBuilderParameterState,
} from './types';
import {
  getBytesLengthFromType,
  getBytesValidationState,
  getParameterStateForBytes,
} from './utils';
import { getReadableSolidityName } from '@/lib/solidity-formatting';

export function BytesInput({
  value,
  name,
  onChange,
  paramAbiFragment,
}: TransactionBuilderParameterInputProps): React.ReactElement {
  const label = getReadableSolidityName(
    paramAbiFragment.name || name,
    'Parameter'
  );

  /**
   * The current validation state of the string
   *
   * Used to display error and warning messages
   */
  const bytesValidationState: BytesValidationState = getBytesValidationState(
    value,
    paramAbiFragment.type
  );

  /**
   * Helper texts to display to the user when they're editing the string
   */
  const helperTexts: Record<BytesValidationState, React.ReactNode> = {
    [BytesValidationState.VALID]: undefined,
    [BytesValidationState.VALID_BUT_EMPTY]: (
      <span className="text-amber-500">{`Empty bytes`}</span>
    ),
    [BytesValidationState.INVALID_NOT_HEX_STRING]:
      '* Must be a hex string starting with 0x',
    [BytesValidationState.INVALID_NOT_BYTES]:
      '* Invalid bytes — must have even number of characters',
    [BytesValidationState.INVALID_INCORRECT_LENGTH]: (() => {
      const numBytes = getBytesLengthFromType(paramAbiFragment.type);
      return (
        <span className="text-amber-500">{`* Must be ${numBytes} bytes long (${
          (numBytes as number) * 2 + 2
        } characters including "0x")`}</span>
      );
    })(),
  };

  /**
   * Event handler for `<FloatingInput/>'s` `onChange` event
   *
   * Updates field value in form provider and calls `onChange` callback
   *
   * @param {React.ChangeEvent<HTMLInputElement>} event `onChange` event
   */
  function handleChange(event: React.ChangeEvent<HTMLInputElement>): void {
    let next = event.target.value;

    // NOTE: Normalize input to always have exactly one leading "0x".
    // This makes pasting more forgiving (e.g., pasting "0x123" when field already has "0x")
    // and keeps the underlying value consistent.

    // Case: user pasted multiple prefixes (e.g., "0x0x123") - strip all "0x" prefixes
    while (next.startsWith('0x')) {
      next = next.slice(2);
    }

    // Case: always prepend exactly one "0x"
    next = '0x' + next;

    const newBytesStringValidationState: BytesValidationState =
      getBytesValidationState(next, paramAbiFragment.type);

    // Case: user entered invalid hex string
    // Note: only want to check invalid hex string case, because if user is typing bytes
    //       we want to let them type valid hex chars, but if instead we were checking it were
    //       valid bytes, we wouldn't be able to let the user type as valid bytes must be even
    //       in length
    if (
      newBytesStringValidationState ===
      BytesValidationState.INVALID_NOT_HEX_STRING
    ) {
      // Prevent changing string if it becomes not a valid hex string
      event.preventDefault();
      event.stopPropagation();
      return;
    }

    // Get new form state
    const newFieldState: TransactionBuilderParameterState =
      getParameterStateForBytes(next, paramAbiFragment.type);

    // Call `onChange` callback
    if (onChange) {
      onChange(name, newFieldState);
    }
  }

  const isError =
    bytesValidationState === BytesValidationState.INVALID_NOT_HEX_STRING ||
    bytesValidationState === BytesValidationState.INVALID_NOT_BYTES;

  const isWarning =
    bytesValidationState === BytesValidationState.VALID_BUT_EMPTY ||
    bytesValidationState === BytesValidationState.INVALID_INCORRECT_LENGTH;

  return (
    <div className="flex flex-col gap-1 w-full">
      <FloatingInput
        value={value}
        onChange={handleChange}
        placeholder="0x"
        label={label}
        className={cn(
          'font-mono',
          isError && 'border-red-500 focus-visible:ring-red-500',
          isWarning && 'border-amber-500 focus-visible:ring-amber-500'
        )}
      />
      {helperTexts[bytesValidationState] && (
        <p className="text-xs px-1">{helperTexts[bytesValidationState]}</p>
      )}
    </div>
  );
}
